import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { UNITS, type UnitSlug } from "@/data/units";
import { REVIEWS } from "@/data/reviews";
import { track } from "@/lib/tracking";

function Stars({ rating }: { rating: number }) {
  return (
    <span role="img" className="flex items-center gap-0.5" aria-label={`Nota ${rating} de 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= Math.round(rating) ? "fill-nook-green text-nook-green" : "text-muted-foreground"}`}
        />
      ))}
    </span>
  );
}

function UnitReviewPanel({ slug }: { slug: UnitSlug }) {
  const unit = UNITS.find((u) => u.slug === slug)!;
  const profile = REVIEWS[slug];
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    trackRef.current?.scrollTo({ left: 0 });
  }, [slug]);

  const scrollTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.children[i] as HTMLElement | undefined;
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
    setIndex(i);
  };

  const move = (dir: 1 | -1) => {
    const next = Math.min(profile.reviews.length - 1, Math.max(0, index + dir));
    scrollTo(next);
  };

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    const closest = children.reduce(
      (best, child, i) =>
        Math.abs(child.offsetLeft - el.offsetLeft - el.scrollLeft) < best.d
          ? { d: Math.abs(child.offsetLeft - el.offsetLeft - el.scrollLeft), i }
          : best,
      { d: Infinity, i: 0 },
    );
    setIndex(closest.i);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        <span className="display text-4xl text-nook-green">{profile.rating.toFixed(1)}</span>
        <Stars rating={profile.rating} />
        <span className="text-sm text-muted-foreground">{unit.shortName}</span>
      </div>

      <div className="group relative mt-8">
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {profile.reviews.map((r) => (
            <figure
              key={r.author}
              className="flex w-[85%] shrink-0 snap-start flex-col rounded-sm border border-border bg-card p-6 sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]"
            >
              <Stars rating={r.rating} />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-nook-green text-[0.7rem] font-semibold tracking-[0.08em] text-nook-green">
                  {r.initials}
                </span>
                <span className="text-xs uppercase tracking-[0.14em] text-foreground">
                  {r.author}
                  <span className="mt-1 block normal-case tracking-normal text-muted-foreground">
                    {r.when}
                  </span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>

        <button
          type="button"
          aria-label="Avaliação anterior"
          onClick={() => move(-1)}
          disabled={index === 0}
          className="absolute left-2 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground opacity-0 transition-opacity hover:text-nook-green disabled:pointer-events-none disabled:opacity-0 group-hover:opacity-100 sm:flex"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Próxima avaliação"
          onClick={() => move(1)}
          disabled={index >= profile.reviews.length - 1}
          className="absolute right-2 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground opacity-0 transition-opacity hover:text-nook-green disabled:pointer-events-none disabled:opacity-0 group-hover:opacity-100 sm:flex"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-1">
        {profile.reviews.map((r, i) => (
          <button
            key={r.author}
            type="button"
            aria-label={`Ir para avaliação ${i + 1}`}
            onClick={() => scrollTo(i)}
            className="flex h-9 w-6 items-center justify-start"
          >
            <span
              aria-hidden="true"
              className={`block h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-nook-green" : "w-1.5 bg-border"
              }`}
            />
          </button>
        ))}
      </div>

    </div>
  );
}

export function Reviews({ only }: { only?: UnitSlug }) {
  const [tab, setTab] = useState<UnitSlug>(only ?? UNITS[0].slug);

  useEffect(() => {
    track("view_reviews", { unit: only ?? "all" });
  }, [only]);

  if (only) return <UnitReviewPanel slug={only} />;

  return (
    <div>
      <div role="tablist" aria-label="Unidades" className="inline-flex rounded-full bg-card p-1">
        {UNITS.map((u) => (
          <button
            key={u.slug}
            role="tab"
            aria-selected={tab === u.slug}
            onClick={() => setTab(u.slug)}
            className={`rounded-full px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] transition-colors sm:px-4 sm:py-2 sm:text-xs ${
              tab === u.slug
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {u.shortName}
          </button>
        ))}
      </div>
      <div className="mt-10">
        <UnitReviewPanel slug={tab} />
      </div>
    </div>
  );
}
